// frontend/src/layouts/user-table/index.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// services
import { listUsers, deleteUser as deleteUserApi } from "services/users";
import { getRoles } from "services/users";

// dialog
import EditUserDialog from "./components/EditUserDialog";

function UserTable() {
  const [allUsers, setAllUsers] = useState([]); // source non filtrée
  const [roles, setRoles] = useState([]); // noms de rôles
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // filtres
  const [searchName, setSearchName] = useState("");
  const [filterRole, setFilterRole] = useState("");

  const navigate = useNavigate();
  const menuOpen = Boolean(anchorEl);

  const reloadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listUsers();
      // on conserve la source brute pour filtrage client
      setAllUsers(data || []);
      setError("");
    } catch (_) {
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // charger rôles + liste
    (async () => {
      try {
        const { data: roleObjs } = await getRoles({ scope: "assignable" }); // ne remonte que ce que l’utilisateur peut assigner
        const normalized = (Array.isArray(roleObjs) ? roleObjs : [])
          .map((r) => (typeof r === "string" ? r : r.value || r.code || r.name || ""))
          .filter(Boolean);
        setRoles(normalized);
      } catch (_) {
        // silencieux
      }
      await reloadUsers();
    })();
  }, [reloadUsers]);

  // Appliquer filtres + fabriquer les rows DataTable
  const users = useMemo(() => {
    const normalizedQuery = searchName.trim().toLowerCase();
    const filtered = (allUsers || []).filter((u) => {
      const matchName =
        !normalizedQuery ||
        (u.username || "").toLowerCase().includes(normalizedQuery) ||
        (u.email || "").toLowerCase().includes(normalizedQuery);
      const matchRole = !filterRole || (u.global_role || "") === filterRole;
      return matchName && matchRole;
    });

    return filtered.map((u) => ({
      id: u.id,
      username: u.username,
      email: u.email,
      role: u.global_role ?? "-",
      actions: (
        <IconButton
          aria-label="actions"
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            setSelectedUserId(u.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    }));
  }, [allUsers, searchName, filterRole]);

  const handleMenuClose = () => {
    // ne pas reset ici (utile pour Edit)
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedUserId) return;
    const res = await deleteUserApi(selectedUserId);
    if (res.ok) {
      setAllUsers((prev) => prev.filter((u) => u.id !== selectedUserId));
      setSuccess("User deleted.");
      setError("");
    } else {
      // ✅ Afficher le message EXACT renvoyé par l’API
      //    (ex: "You can not delete your own account." / "This user can not be delete.")
      setError(res.error || "Failed to delete user.");
      setSuccess("");
    }
    setSelectedUserId(null);
    handleMenuClose();
  };

  const handleEdit = () => {
    if (!selectedUserId) return;
    setEditOpen(true);
    handleMenuClose();
  };

  const handleGoToAddUser = () => navigate("/add-user");

  const columns = [
    { Header: "Username", accessor: "username", align: "left" },
    { Header: "Email", accessor: "email", align: "left" },
    { Header: "Role", accessor: "role", align: "left" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  User Table
                </MDTypography>
              </MDBox>

              {/* ───────────────── Toolbar filtres + bouton ───────────────── */}
              <MDBox px={2} pt={2} pb={1} display="flex" alignItems="center">
                {/* Spacer gauche */}
                <MDBox flex={1} />

                {/* Centre : filtres */}
                <MDBox
                  flex={1}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <TextField
                    label="Search by name or email"
                    size="small"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="filter-role-label">Filter by role</InputLabel>
                    <Select
                      labelId="filter-role-label"
                      label="Filter by role"
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All roles</em>
                      </MenuItem>
                      {roles.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Droite : bouton Add User */}
                <MDBox flex={1} display="flex" justifyContent="flex-end">
                  <Button variant="contained" color="info" onClick={handleGoToAddUser}>
                    Add User
                  </Button>
                </MDBox>
              </MDBox>
              {/* ──────────────────────────────────────────────────────────── */}

              <MDBox pt={1} pb={2}>
                {loading && <p style={{ marginLeft: 16 }}>Loading…</p>}
                {error && <p style={{ color: "red", marginLeft: 16 }}>{error}</p>}
                {success && <p style={{ color: "green", marginLeft: 16 }}>{success}</p>}

                <DataTable
                  table={{ columns, rows: users }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />

                {/* Menu actions (Edit / Delete uniquement) */}
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={handleEdit}>Edit</MenuItem>
                  <MenuItem onClick={handleDelete}>Delete</MenuItem>
                </Menu>

                {/* Modale d'édition */}
                <EditUserDialog
                  open={editOpen}
                  userId={selectedUserId}
                  availableRoles={roles}
                  onClose={() => {
                    setEditOpen(false);
                    setSelectedUserId(null);
                  }}
                  onSaved={() => {
                    setEditOpen(false);
                    setSelectedUserId(null);
                    reloadUsers(); // rafraîchit la source puis les filtres s'appliquent
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default UserTable;
